import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Icon } from "./components";

interface Props {
  children: ReactNode;
  /**
   * `app` (Standard): äußerstes Netz in main.tsx, füllt den ganzen Bildschirm.
   * `inhalt`: sitzt in der AppShell um den Seiteninhalt — Seitenleiste und Kopf bleiben bedienbar.
   */
  bereich?: "app" | "inhalt";
}

/** Was die Karte zeigt. `pruefen` = Netzfehler erkannt, Erholung (Versionsvergleich/Neuladen) läuft. */
export type FehlerArt = "programm" | "netz" | "offline" | "neue-version" | "pruefen";

interface State {
  fehler: Error | null;
  art: FehlerArt;
}

const NETZ_MUSTER =
  /failed to fetch|networkerror|load failed|network request failed|unable to connect to the server|you are offline/i;
const CHUNK_MUSTER =
  /dynamically imported module|importing a module script failed|chunkloaderror|loading chunk \S+ failed|unable to preload css/i;

/** Sammelt Meldung, Ursache (`cause`) und Einzelfehler (`errors`, z. B. Stack Auths RetryError). */
function fehlerText(e: unknown, tiefe = 0): string {
  if (!(e instanceof Error) || tiefe > 4) return String(e ?? "");
  const teile = [e.name, e.message];
  if (e.cause) teile.push(fehlerText(e.cause, tiefe + 1));
  const einzel = (e as { errors?: unknown[] }).errors;
  if (Array.isArray(einzel)) for (const x of einzel) teile.push(fehlerText(x, tiefe + 1));
  return teile.join(" ");
}

/** Netz- oder Nachladefehler (still behebbar) oder echter Programmfehler? */
export function fehlerArtVon(e: unknown): "netz" | "chunk" | "programm" {
  const text = fehlerText(e);
  if (CHUNK_MUSTER.test(text)) return "chunk";
  if (NETZ_MUSTER.test(text)) return "netz";
  return "programm";
}

const NEULADEN_SCHLUESSEL = "maestro:auto-neuladen";
const NEULADEN_SPERRE_MS = 30_000;

/**
 * Schleifenschutz: höchstens ein stilles Neuladen je 30 s und Tab. Gibt `true` zurück und merkt
 * sich den Zeitpunkt, wenn neu geladen werden darf. Ohne sessionStorage (gesperrt) nie still laden.
 */
export function darfStillNeuLaden(jetzt = Date.now()): boolean {
  try {
    const zuletzt = Number(sessionStorage.getItem(NEULADEN_SCHLUESSEL) ?? 0);
    if (jetzt - zuletzt < NEULADEN_SPERRE_MS) return false;
    sessionStorage.setItem(NEULADEN_SCHLUESSEL, String(jetzt));
    return true;
  } catch {
    return false;
  }
}

/**
 * Wurde neu deployt? Vergleicht das geladene Haupt-Skript (`/assets/index-<hash>.js`) mit der
 * aktuellen index.html. `null` = nicht feststellbar (Server nicht erreichbar, Dev-Server).
 */
export async function neueVersionDa(): Promise<boolean | null> {
  const skript = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  if (!skript) return null;
  try {
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return null;
    return !(await res.text()).includes(new URL(skript.src).pathname);
  } catch {
    return null;
  }
}

const TEXTE: Record<Exclude<FehlerArt, "pruefen">, { icon: string; titel: string; hinweis: string }> = {
  programm: {
    icon: "error",
    titel: "Diese Ansicht konnte nicht angezeigt werden",
    hinweis:
      "Beim Anzeigen ist ein Fehler aufgetreten. Neu laden hilft meistens. Tritt er wieder auf, schick uns bitte die technischen Details.",
  },
  netz: {
    icon: "cloud_off",
    titel: "Verbindung kurz unterbrochen",
    hinweis: "Der Server war gerade nicht erreichbar. Lade die Seite neu, sobald die Verbindung wieder steht.",
  },
  offline: {
    icon: "wifi_off",
    titel: "Keine Internetverbindung",
    hinweis: "Sobald sie zurück ist, lädt die Seite von selbst neu.",
  },
  "neue-version": {
    icon: "system_update_alt",
    titel: "Neue Version verfügbar",
    hinweis: "MAESTRO wurde gerade aktualisiert. Die Seite wird aktualisiert …",
  },
};

/**
 * Sicherheitsnetz gegen einen weißen Bildschirm (seit Bughunt 02.09.2026). Fängt Render-/Lifecycle-
 * Fehler im Kindbaum (keine Event-Handler-Fehler).
 *
 * Seit 28.09.2026 (Befund Antoine: rohe „Error after 1 attempts … Failed to fetch“-Meldung neben
 * dem Knopf, nach einem Deploy bei offenem Tab): Netz- und Nachladefehler lösen erst einen stillen
 * Neuladeversuch aus (Schleifenschutz `darfStillNeuLaden`), offline wartet die Karte auf `online`,
 * ein erkannter Deploy heißt „Neue Version verfügbar“. Technische Details nur aufgeklappt.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { fehler: null, art: "programm" };

  static getDerivedStateFromError(fehler: Error): Partial<State> {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return { fehler, art: "offline" };
    return { fehler, art: fehlerArtVon(fehler) === "programm" ? "programm" : "pruefen" };
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo) {
    if (this.state.art === "programm") {
      console.error("Unerwarteter Fehler in der Oberflaeche:", fehler, info.componentStack);
      return;
    }
    window.addEventListener("online", this.beiOnline);
    if (this.state.art === "pruefen") void this.erholen();
  }

  override componentWillUnmount() {
    window.removeEventListener("online", this.beiOnline);
  }

  private beiOnline = () => window.location.reload();

  private async erholen() {
    const neu = await neueVersionDa();
    if (navigator.onLine === false) {
      this.setState({ art: "offline" });
      return;
    }
    if (darfStillNeuLaden()) {
      this.setState({ art: neu ? "neue-version" : "pruefen" });
      window.location.reload();
      return;
    }
    this.setState({ art: neu ? "neue-version" : "netz" });
  }

  override render() {
    const { fehler, art } = this.state;
    if (!fehler) return this.props.children;
    const rahmen =
      this.props.bereich === "inhalt"
        ? "flex justify-center py-10 md:py-16"
        : "flex min-h-screen items-center justify-center bg-background px-4 py-10";
    return (
      <div className={rahmen}>
        {art === "pruefen" ? (
          <p role="status" className="flex items-center gap-2 text-[13px] text-secondary">
            <Icon name="progress_activity" className="animate-spin text-[18px]" /> Verbindung wird wiederhergestellt …
          </p>
        ) : (
          <FehlerKarte art={art} fehler={fehler} />
        )}
      </div>
    );
  }
}

function FehlerKarte({ art, fehler }: { art: Exclude<FehlerArt, "pruefen">; fehler: Error }) {
  const t = TEXTE[art];
  const details = `${fehler.name}: ${fehler.message}\n\n${window.location.href}\n${new Date().toISOString()}\n\n${fehler.stack ?? ""}`.trim();
  return (
    <div
      role={art === "programm" ? "alert" : "status"}
      data-testid={`fehleransicht-${art}`}
      className="w-full min-w-0 max-w-md rounded-[14px] border border-[#EFE8DA] bg-white p-6 text-center shadow-sm dark:border-white/[0.08] dark:bg-surface-container-lowest md:p-7"
    >
      <div
        className={`mx-auto grid h-11 w-11 place-items-center rounded-full ${
          art === "programm"
            ? "bg-[#F8E7E3] text-[#B23B32] dark:bg-[#B23B32]/22 dark:text-[#e39b92]"
            : "bg-[#F6F1E7] text-[#6F675A] dark:bg-white/[0.06] dark:text-[#b8ae9e]"
        }`}
      >
        <Icon name={t.icon} className="text-[22px]" />
      </div>
      <h1 className="mt-4 text-[16px] font-bold text-on-background dark:text-inverse-on-surface">{t.titel}</h1>
      <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-[#6F675A] dark:text-[#b8ae9e]">{t.hinweis}</p>
      {art !== "offline" && (
        <Button type="button" className="mt-5" onClick={() => window.location.reload()}>
          <Icon name="refresh" className="text-[16px]" /> Seite neu laden
        </Button>
      )}
      {art !== "neue-version" && (
        <details className="group mt-5 border-t border-[#F2ECDF] pt-3 text-left dark:border-white/[0.06]">
          <summary className="flex cursor-pointer list-none items-center justify-center gap-1 text-[12px] font-semibold text-secondary hover:text-on-background [&::-webkit-details-marker]:hidden">
            Technische Details
            <Icon name="expand_more" className="text-[16px] transition-transform group-open:rotate-180" />
          </summary>
          <pre className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-[#F6F1E7] p-3 font-mono text-[11px] leading-snug text-[#5B5347] [overflow-wrap:anywhere] dark:bg-white/[0.05] dark:text-[#b8ae9e]">
            {details}
          </pre>
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              className="!py-1.5 !text-[12px]"
              onClick={() => void navigator.clipboard?.writeText(details).catch(() => undefined)}
            >
              <Icon name="content_copy" className="text-[14px]" /> Details kopieren
            </Button>
          </div>
        </details>
      )}
    </div>
  );
}
